/**
 * router/index.ts
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router';

// constants
import routes from '@/router/routes';
import { useAppStore } from '@/stores/app';
import { packageAuthService } from '@/api';
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard';
import { watchForStaleBuild } from '@/helpers/staleBuild';

const publicRoutes = new Set([
  'login',
  'callback',
  'PageNotFound',
  'NoAccess'
]);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

router.beforeEach(async (to, from, next) => {
  // Headless renderer routes (worker-driven) bypass user auth; they gate
  // themselves on the render worker's `sx` query key and puppeteerSecretKey
  // cookie instead (see pages/LiteRender/liteRender.vue). This must run before
  // the token check: the worker only sets dx_access_token after the page loads.
  if (to.meta?.Headless) {
    next();
    return;
  }

  const isPublic =
    publicRoutes.has(to.name as string) ||
    to.path === '/login' ||
    to.path === '/callback' ||
    to.path === '/PageNotFound' ||
    to.path === '/no-access';

  const token = packageAuthService.getAccessToken();

  if (!token && !isPublic) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  if (token && to.name !== 'NoAccess') {
    const appStore = useAppStore();
    if (!appStore.user) {
      try {
        const { userService } = await import('@/services/api/userService');
        const user = await userService.getCurrentUser();
        if (user) {
          appStore.setUser(user);
        }
      } catch (error) {
        const { isForbiddenPortalError } = await import('@/api/errorHandler');
        if (isForbiddenPortalError(error)) {
          next({ name: 'NoAccess', replace: true });
          return;
        }
      }
    }
  }

  // Redirect root to profile selection
  if (to.path === '/') {
    next({ name: 'ProfileSelection' });
    return;
  }

  next();
});

// Unsaved changes guard – prompt when leaving/switching editor templates
router.beforeEach(async (to, from) => {
  const isFromEditor = from.path.startsWith('/app/');
  if (!isFromEditor) return;

  // Allow navigation within the same template (query changes, etc.)
  if (to.path === from.path) return;

  const { confirmNavigation } = useUnsavedChangesGuard();
  const result = await confirmNavigation();
  if (result === 'cancel') return false;
});

watchForStaleBuild(router);

export default router;
